import TIcon from './../t-icon/index';
import dispatch from '../../utils/event';
import { beautifyInputValue, formatThousandsValue } from './../../utils/input';
import { validatorByIconSize, validatorByInputType, validatorByTextAlign } from './../../utils/validator';
import { defineComponent, computed, ref, getCurrentInstance, nextTick, watch, onBeforeUnmount, } from 'vue';
export default defineComponent({
    name: 'TInput',
    components: { TIcon },
    props: {
        modelValue: {
            type: String,
            default: '',
        },
        hasFontface: {
            type: Boolean,
            default: false,
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        hasBorder: {
            type: Boolean,
            default: true,
        },
        spellcheck: {
            type: Boolean,
            default: false,
        },
        maxlength: {
            type: Number,
            default: 20,
        },
        tips: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        suffix: {
            type: String,
            default: '',
        },
        prefix: {
            type: String,
            default: '',
        },
        hasBold: {
            type: Boolean,
            default: false,
        },
        theme: {
            type: String,
            default: 'default',
            validator: (value) => ['default', 'success', 'error', 'warning'].includes(value),
        },
        type: {
            type: String,
            default: 'text',
            validator: (value) => validatorByInputType(value),
        },
        size: {
            type: String,
            default: 'large',
            validator: (value) => validatorByIconSize(value),
        },
        placeholderSize: {
            type: String,
            default: '',
            validator: (value) => value === '' || validatorByIconSize(value),
        },
        textAlign: {
            type: String,
            default: 'left',
            validator: (value) => validatorByTextAlign(value),
        },
        isFormatter: {
            type: Boolean,
            default: false,
        },
        toFixed: {
            type: Number,
            default: 2,
        },
    },
    emits: ['click', 'update:modelValue', 'input', 'focus', 'blur', 'change'],
    setup(props, context) {
        /** 组件的className前辍 */
        const prefixCls = ref('t-input');
        /** 获取组件的上下文 */
        const instance = getCurrentInstance();
        /** 当前组件是否处于focus状态 */
        const isFocus = ref(false);
        /** 输入框的值(因为需要兼容到千分位分隔的特殊场景) */
        const myInputValue = ref(props.isFormatter ? formatThousandsValue(props.modelValue, props.toFixed) : props.modelValue);
        /** 输入框类型(因为需要兼容到千分位分隔的特殊场景) */
        const inputType = props.isFormatter && props.type !== 'tel' ? 'text' : props.type;
        /** 模板依赖的className */
        const getClasses = computed(() => {
            return {
                wrap: [prefixCls.value],
                inputControl: [
                    `${prefixCls.value}-control`,
                    `${prefixCls.value}-size__${props.size}`,
                    getFormItemName.value === 'TFormItem'
                        ? `${prefixCls.value}-align__right`
                        : `${prefixCls.value}-align__${props.textAlign}`,
                    props.hasFontface && `${prefixCls.value}-font`,
                    props.hasBold && `${prefixCls.value}-font__bold`,
                    props.placeholderSize && `${prefixCls.value}-ph__${props.placeholderSize}`,
                ],
                suffix: [`${prefixCls.value}-suffix`],
                prefix: [`${prefixCls.value}-prefix`],
                footer: [`${prefixCls.value}-ft`],
                tips: [`${prefixCls.value}-tips`, `${prefixCls.value}-theme__${props.theme}`],
                border: [
                    `${prefixCls.value}-border`,
                    `${prefixCls.value}-border__${props.theme}`,
                    (isFocus.value || props.modelValue !== '') && `${prefixCls.value}-border__edited`,
                ],
            };
        });
        /** 判断组件是否被TFormItem组件包裹着 */
        const getFormItemName = computed(() => {
            if (instance) {
                let parent = instance.parent;
                let parentName = parent.type.name || '';
                while (parent && parentName !== 'TFormItem') {
                    parent = parent.parent;
                    parentName = (parent && parent.type.name) || '';
                }
                return parentName;
            }
            return '';
        });
        /** 找到输入框的父级元素，并且标签名称是“TFormItem” */
        const getFormItemParent = computed(() => {
            if (instance) {
                let parent = instance.parent;
                let parentName = parent.type.name || '';
                while (parent && parentName !== 'TFormItem') {
                    parent = parent.parent;
                    parentName = (parent && parent.type.name) || '';
                }
                return parent;
            }
            return '';
        });
        /** 对外响应的事件 */
        const listener = (event) => context.emit('click', event);
        const emitEvent = (name, event) => {
            if (!props.isFormatter) {
                context.emit(name, event.target.value, event);
                return;
            }
            context.emit(name, formatThousandsValue(event.target.value, props.toFixed), event, beautifyInputValue(event.target.value, props.toFixed));
        };
        const blur = () => instance.refs.input.blur();
        const focus = () => instance.refs.input.focus();
        const onInputHandler = (event) => {
            emitEvent('update:modelValue', event);
            if (!props.isFormatter) {
                myInputValue.value = event.target.value;
            }
            else {
                const tempValue = event.target.value;
                myInputValue.value = '%20';
                nextTick(() => (myInputValue.value = formatThousandsValue(tempValue, props.toFixed)));
            }
            nextTick(() => {
                // 对外暴露的input事件
                emitEvent('input', event);
                // 跨组件通信，通知t-form-item，输入框已经获得焦点
                dispatch.emit('form.item.change', {
                    parentUid: getFormItemParent?.value?.uid || 0,
                    value: myInputValue.value,
                });
            });
        };
        const onFocusHandler = (event) => {
            isFocus.value = true;
            emitEvent('focus', event);
            // 跨组件通信，通知t-form-item，输入框的值已经focus了
            dispatch.emit('form.item.focus', {
                parentUid: getFormItemParent?.value?.uid || 0,
                value: event.target.value,
            });
        };
        const onBlurHandler = (event) => {
            isFocus.value = false;
            emitEvent('blur', event);
            // 跨组件通信，通知t-form-item，输入框的值已经blur了
            dispatch.emit('form.item.blur', {
                parentUid: getFormItemParent?.value?.uid || 0,
                value: event.target.value,
            });
        };
        const onChangeHandler = (event) => emitEvent('change', event);
        /** 监听特定的值, 做组修的内部的显示、隐藏逻辑 */
        const stopWatchModelValue = watch(() => props.modelValue, (newValue, oldValue) => {
            if (props.isFormatter && newValue === oldValue) {
                return;
            }
            onInputHandler({
                target: {
                    value: newValue,
                },
            });
        });
        onBeforeUnmount(() => stopWatchModelValue());
        return {
            prefixCls,
            getClasses,
            isFocus,
            myInputValue,
            inputType,
            listener,
            getFormItemName,
            blur,
            focus,
            onInputHandler,
            onFocusHandler,
            onBlurHandler,
            onChangeHandler,
        };
    },
});
